import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { FirebaseFirestore } from '@capacitor-firebase/firestore';
import { FirebaseFunctions } from '@capacitor-firebase/functions';
import { initializeApp, getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { firebaseConfig, STORAGE_EMULATOR_HOST } from './firebaseConfig';
import type {
  DevCreateCustomTokenPayload,
  DevCreateCustomTokenResponse,
  DevRemoveParticipationPayload,
  InitializePreScreeningPayload,
  InitializePreScreeningResponse,
  NotificationTemplatePayload,
  NotificationTemplateResult,
  SubmitQuestionnairePayload,
  SubmitQuestionnaireResponse,
} from '@hub/common';

if (!Capacitor.isNativePlatform()) {
  initializeApp(firebaseConfig);
}

if (import.meta.env.MODE === 'development') {
  const host =
    import.meta.env.PUBLIC_FIREBASE_ANDROID_EMULATOR_HOST ?? 'localhost';

  if (Capacitor.isNativePlatform()) {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- this is no hook
    void FirebaseAuthentication.useEmulator({
      host,
      port: 4010,
      scheme: 'http',
    });
    // eslint-disable-next-line react-hooks/rules-of-hooks -- this is no hook
    void FirebaseFirestore.useEmulator({
      host,
      port: 4011,
    });
    // eslint-disable-next-line react-hooks/rules-of-hooks -- this is no hook
    void FirebaseFunctions.useEmulator({
      host,
      port: 5001,
    });
  } else {
    // getAuth, getFirestore, getFunctions and getStorage connect every
    // instance they create to these hosts, so the instances the Capacitor web
    // plugins create start on the emulators.
    Object.assign(globalThis, {
      __FIREBASE_DEFAULTS__: {
        emulatorHosts: {
          auth: `${host}:4010`,
          firestore: `${host}:4011`,
          functions: `${host}:5001`,
          // Consider @capacitor-firebase/storage (uploading to storage is used by the admin protocol publish flow)
          storage: STORAGE_EMULATOR_HOST,
        },
      },
    });
  }
}

// Capactitor Functions emulator is buggy, if region is set on functions, emulator produce functions url that points
// to cloud deployed functions, not to localhost. So here We set the region only when not in development mode
// and when run emulator we deploy functions to the default us-central1 region - root package.json emulate command
const regionConfig =
  import.meta.env.MODE === 'development'
    ? {}
    : {
        region: import.meta.env.PUBLIC_FIREBASE_CLOUD_FUNCTIONS_REGION,
      };

// callable functions
export const devCreateCustomTokenFunction = (
  data: DevCreateCustomTokenPayload
) =>
  FirebaseFunctions.callByName<
    DevCreateCustomTokenPayload,
    DevCreateCustomTokenResponse
  >({
    name: 'devGroupA-devCreateCustomToken',
    data,
    ...regionConfig,
  });

export const devRemoveParticipationFunction = (
  data: DevRemoveParticipationPayload
) =>
  FirebaseFunctions.callByName<DevRemoveParticipationPayload, null>({
    name: 'devGroupA-devRemoveParticipation',
    data,
    ...regionConfig,
  });

export const devRemoveAllParticipationsFunction = () =>
  FirebaseFunctions.callByName({
    name: 'devGroupA-devRemoveAllParticipations',
    ...regionConfig,
  });

export const initializePreScreening = (data: InitializePreScreeningPayload) =>
  FirebaseFunctions.callByName<
    InitializePreScreeningPayload,
    InitializePreScreeningResponse
  >({
    name: 'groupA-initializePreScreening',
    data,
    ...regionConfig,
  });

export const submitQuestionnaireFunction = (data: SubmitQuestionnairePayload) =>
  FirebaseFunctions.callByName<
    SubmitQuestionnairePayload,
    SubmitQuestionnaireResponse
  >({
    name: 'groupA-submitQuestionnaire',
    data,
    ...regionConfig,
  });

export const publishNotificationTemplateFunction = async (
  data: NotificationTemplatePayload
): Promise<{ data: NotificationTemplateResult }> => {
  const region =
    import.meta.env.MODE === 'development'
      ? undefined
      : import.meta.env.PUBLIC_FIREBASE_CLOUD_FUNCTIONS_REGION;

  const functions = getFunctions(getApp(), region);
  const callable = httpsCallable<
    NotificationTemplatePayload,
    NotificationTemplateResult
  >(functions, 'groupA-publishNotificationTemplate', { timeout: 30000 });

  const result = await callable(data);
  return { data: result.data };
};
